import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Card,
  Header,
  Icon,
  Segment,
  Grid,
  Statistic,
  Menu,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';
import logoSvg from '../logo.svg';

/**
 * Multilingual home page — renders at /en, /sv, /de, /zh.
 * Each language root shows the landing experience in that language.
 */

const i18n = {
  en: {
    tagline: 'Translation quality infrastructure for the Plone ecosystem.',
    detail: '70 languages. 330,000+ translations. ISO 17100 conventions. Risk management. Audit trail.',
    builtBy: 'Built by a Swede and an AI in a lighthouse in Malm\u00f6.',
    builtOn: 'Built on',
    statsLanguages: 'Languages',
    statsPlatforms: 'Platforms',
    statsTranslations: 'Translations',
    sectionMonster: 'The Monster Machine',
    sectionMonsterDesc: 'Coverage, conventions, glossary, QC findings, risk management. The beating heart of translation quality.',
    sectionKb: 'Knowledge Base',
    sectionKbDesc: '1,000+ documents. Architecture decisions, regulatory compliance, linguistic strategies. Full-text searchable.',
    sectionQms: 'Quality Management',
    sectionQmsDesc: 'Document register, review schedule, 36 external standards, issue tracker, training records.',
    startExploring: 'Start Exploring',
    startSub: 'The interesting bits. Each page works on its own.',
    highlightMap: 'Who Maintains What',
    highlightMapSub: 'Which Plone integrators maintain which languages — and why it matters for .po quality.',
    highlightCoverage: 'Coverage Heat Map',
    highlightCoverageSub: 'How complete is each language? Where are the gaps? Five platforms, seventy languages, one view.',
    highlightConv: 'Language Conventions',
    highlightConvSub: 'Formal or informal? Imperative or infinitive? Every language has rules. We wrote them down for 70.',
    highlightGlossary: 'Terminology Glossary',
    highlightGlossarySub: '193k IATE entries + 2.5M Microsoft terms. Search any UI word in any language.',
    footer: 'Malm\u00f6, Sweden',
    pronunciation: '/\u2009lj\u00fc:sa t\u00ed:der\u2009/ \u2014 Swedish for \u201cbright times\u201d',
  },
  sv: {
    tagline: '\u00d6vers\u00e4ttningskvalitet f\u00f6r Plone-ekosystemet.',
    detail: '70 spr\u00e5k. 330 000+ \u00f6vers\u00e4ttningar. ISO 17100-konventioner. Riskhantering. Revisionssl\u00e4pning.',
    builtBy: 'Byggt av en svensk och en AI i en fyr i Malm\u00f6.',
    builtOn: 'Byggt p\u00e5',
    statsLanguages: 'Spr\u00e5k',
    statsPlatforms: 'Plattformar',
    statsTranslations: '\u00d6vers\u00e4ttningar',
    sectionMonster: 'Monstermaskinen',
    sectionMonsterDesc: 'T\u00e4ckning, konventioner, ordlistor, QC-fynd, riskhantering. Hj\u00e4rtat i \u00f6vers\u00e4ttningskvaliteten.',
    sectionKb: 'Kunskapsbank',
    sectionKbDesc: '1 000+ dokument. Arkitekturbeslut, regulatorisk efterlevnad, lingvistiska strategier. Fulltexts\u00f6kbar.',
    sectionQms: 'Kvalitetsledning',
    sectionQmsDesc: 'Dokumentregister, granskningsschema, 36 externa standarder, \u00e4rendehantering, utbildningsprotokoll.',
    startExploring: 'B\u00f6rja utforska',
    startSub: 'De intressanta delarna. Varje sida fungerar f\u00f6r sig.',
    highlightMap: 'Vem underh\u00e5ller vad',
    highlightMapSub: 'Vilka Plone-integrat\u00f6rer underh\u00e5ller vilka spr\u00e5k \u2014 och varf\u00f6r det spelar roll f\u00f6r .po-kvaliteten.',
    highlightCoverage: 'T\u00e4ckningskarta',
    highlightCoverageSub: 'Hur komplett \u00e4r varje spr\u00e5k? Var finns l\u00fcckorna? Fem plattformar, sjuttio spr\u00e5k, en vy.',
    highlightConv: 'Spr\u00e5kkonventioner',
    highlightConvSub: 'Formellt eller informellt? Imperativ eller infinitiv? Varje spr\u00e5k har regler. Vi skrev ner dem f\u00f6r 70.',
    highlightGlossary: 'Terminologiordbok',
    highlightGlossarySub: '193 000 IATE-termer + 2,5 miljoner Microsoft-termer. S\u00f6k vilket UI-ord som helst p\u00e5 vilket spr\u00e5k som helst.',
    footer: 'Malm\u00f6, Sverige',
    pronunciation: '/\u2009lj\u00fc:sa t\u00ed:der\u2009/ \u2014 svenska: \u201dljusa tider\u201d',
  },
  de: {
    tagline: '\u00dcbersetzungsqualit\u00e4t f\u00fcr das Plone-\u00d6kosystem.',
    detail: '70 Sprachen. 330.000+ \u00dcbersetzungen. ISO 17100-Konventionen. Risikomanagement. Audit-Trail.',
    builtBy: 'Gebaut von einem Schweden und einer KI in einem Leuchtturm in Malm\u00f6.',
    builtOn: 'Gebaut mit',
    statsLanguages: 'Sprachen',
    statsPlatforms: 'Plattformen',
    statsTranslations: '\u00dcbersetzungen',
    sectionMonster: 'Die Monstermaschine',
    sectionMonsterDesc: 'Abdeckung, Konventionen, Glossar, QC-Befunde, Risikomanagement. Das Herzst\u00fcck der \u00dcbersetzungsqualit\u00e4t.',
    sectionKb: 'Wissensdatenbank',
    sectionKbDesc: '1.000+ Dokumente. Architekturentscheidungen, regulatorische Compliance, linguistische Strategien. Volltextsuche.',
    sectionQms: 'Qualit\u00e4tsmanagement',
    sectionQmsDesc: 'Dokumentenregister, Pr\u00fcfplan, 36 externe Standards, Issue-Tracker, Schulungsprotokolle.',
    startExploring: 'Entdecken',
    startSub: 'Die interessanten Teile. Jede Seite funktioniert eigenst\u00e4ndig.',
    highlightMap: 'Wer pflegt was',
    highlightMapSub: 'Welche Plone-Integratoren pflegen welche Sprachen \u2014 und warum das f\u00fcr die .po-Qualit\u00e4t wichtig ist.',
    highlightCoverage: 'Abdeckungskarte',
    highlightCoverageSub: 'Wie vollst\u00e4ndig ist jede Sprache? Wo sind die L\u00fccken? F\u00fcnf Plattformen, siebzig Sprachen, eine Ansicht.',
    highlightConv: 'Sprachkonventionen',
    highlightConvSub: 'Formell oder informell? Imperativ oder Infinitiv? Jede Sprache hat Regeln. Wir haben sie f\u00fcr 70 aufgeschrieben.',
    highlightGlossary: 'Terminologie-Glossar',
    highlightGlossarySub: '193.000 IATE-Eintr\u00e4ge + 2,5 Mio. Microsoft-Terme. Jedes UI-Wort in jeder Sprache suchen.',
    footer: 'Malm\u00f6, Schweden',
    pronunciation: '/\u2009lj\u00fc:sa t\u00ed:der\u2009/ \u2014 Schwedisch: \u201eLichte Zeiten\u201c',
  },
  zh: {
    tagline: 'Plone\u751f\u6001\u7cfb\u7edf\u7684\u7ffb\u8bd1\u8d28\u91cf\u57fa\u7840\u8bbe\u65bd\u3002',
    detail: '70\u79cd\u8bed\u8a00\u300233\u4e07+\u7ffb\u8bd1\u3002ISO 17100\u89c4\u8303\u3002\u98ce\u9669\u7ba1\u7406\u3002\u5ba1\u8ba1\u8ddf\u8e2a\u3002',
    builtBy: '\u7531\u4e00\u4e2a\u745e\u5178\u4eba\u548c\u4e00\u4e2aAI\u5728\u9a6c\u5c14\u9ed8\u7684\u706f\u5854\u91cc\u6784\u5efa\u3002',
    builtOn: '\u6784\u5efa\u4e8e',
    statsLanguages: '\u8bed\u8a00',
    statsPlatforms: '\u5e73\u53f0',
    statsTranslations: '\u7ffb\u8bd1',
    sectionMonster: '\u602a\u517d\u673a\u5668',
    sectionMonsterDesc: '\u8986\u76d6\u7387\u3001\u89c4\u8303\u3001\u672f\u8bed\u8868\u3001QC\u53d1\u73b0\u3001\u98ce\u9669\u7ba1\u7406\u3002\u7ffb\u8bd1\u8d28\u91cf\u7684\u6838\u5fc3\u3002',
    sectionKb: '\u77e5\u8bc6\u5e93',
    sectionKbDesc: '1000+\u6587\u6863\u3002\u67b6\u6784\u51b3\u7b56\u3001\u6cd5\u89c4\u5408\u89c4\u3001\u8bed\u8a00\u5b66\u7b56\u7565\u3002\u5168\u6587\u641c\u7d22\u3002',
    sectionQms: '\u8d28\u91cf\u7ba1\u7406',
    sectionQmsDesc: '\u6587\u6863\u767b\u8bb0\u3001\u5ba1\u67e5\u8ba1\u5212\u300136\u9879\u5916\u90e8\u6807\u51c6\u3001\u95ee\u9898\u8ddf\u8e2a\u3001\u57f9\u8bad\u8bb0\u5f55\u3002',
    startExploring: '\u5f00\u59cb\u63a2\u7d22',
    startSub: '\u6700\u6709\u8da3\u7684\u90e8\u5206\u3002\u6bcf\u4e2a\u9875\u9762\u72ec\u7acb\u5de5\u4f5c\u3002',
    highlightMap: '\u8c01\u5728\u7ef4\u62a4\u4ec0\u4e48',
    highlightMapSub: '\u54ea\u4e9bPlone\u96c6\u6210\u5546\u7ef4\u62a4\u54ea\u4e9b\u8bed\u8a00\u2014\u2014\u4ee5\u53ca\u8fd9\u5bf9.po\u8d28\u91cf\u7684\u610f\u4e49\u3002',
    highlightCoverage: '\u8986\u76d6\u7387\u70ed\u56fe',
    highlightCoverageSub: '\u6bcf\u79cd\u8bed\u8a00\u7684\u5b8c\u6210\u5ea6\u5982\u4f55\uff1f\u5dee\u8ddd\u5728\u54ea\u91cc\uff1f\u4e94\u4e2a\u5e73\u53f0\uff0c\u4e03\u5341\u79cd\u8bed\u8a00\uff0c\u4e00\u4e2a\u89c6\u56fe\u3002',
    highlightConv: '\u8bed\u8a00\u89c4\u8303',
    highlightConvSub: '\u6b63\u5f0f\u8fd8\u662f\u975e\u6b63\u5f0f\uff1f\u547d\u4ee4\u5f0f\u8fd8\u662f\u4e0d\u5b9a\u5f0f\uff1f\u6bcf\u79cd\u8bed\u8a00\u90fd\u6709\u89c4\u5219\u3002\u6211\u4eec\u4e3a70\u79cd\u8bed\u8a00\u5199\u4e0b\u4e86\u5b83\u4eec\u3002',
    highlightGlossary: '\u672f\u8bed\u8bcd\u5178',
    highlightGlossarySub: '19.3\u4e07IATE\u6761\u76ee + 250\u4e07\u5fae\u8f6f\u672f\u8bed\u3002\u641c\u7d22\u4efb\u4f55\u8bed\u8a00\u7684\u4efb\u4f55UI\u8bcd\u6c47\u3002',
    footer: '\u9a6c\u5c14\u9ed8\uff0c\u745e\u5178',
    pronunciation: '/\u2009lj\u00fc:sa t\u00ed:der\u2009/ \u2014 \u745e\u5178\u8bed\uff1a\u201c\u5149\u660e\u65f6\u4ee3\u201d',
  },
};

const funFacts = {
  en: [
    'Basque (eu) has complete Plone translations \u2014 for a language older than every Indo-European family on the continent.',
    'The German .po files have 20+ independent companies checking them. Italian has one \u2014 but it\'s RedTurtle, and they\'re magnificent.',
    'We track translations in Burmese. Myanmar script has 57 special characters. We have conventions for all of them.',
    'Plone\'s Swedish translations had no commercial maintainer. So we became one.',
    '"Ljusa Tider" sounds like an IKEA shelf. It means "bright times." We build translation infrastructure, not furniture. (Well, mostly.)',
    'We have language conventions for 70 languages including the formal/informal address rules. In Swedish, we abolished formal address in 1967. Du-reformen.',
    'ISO 17100 says translation quality matters. ISO 14971 says risk matters. We made them talk to each other.',
    'Softcatal\u00e0 promotes Catalan in software. UZEI does the same for Basque. CodeSyntax ships both in Plone. Minority languages punch above their weight.',
    'J\u00f6rg Zell from Interaktiv (Platinum Plone Sponsor) says even German has missing translations in real projects. If the best-maintained language has gaps, imagine the rest.',
    'There are 193,000 IATE terminology entries in the glossary. The EU really likes defining things.',
  ],
  sv: [
    'Baskiska (eu) har kompletta Plone-\u00f6vers\u00e4ttningar \u2014 f\u00f6r ett spr\u00e5k \u00e4ldre \u00e4n alla indoeuropeiska spr\u00e5k p\u00e5 kontinenten.',
    'Tyska .po-filer granskas av 20+ oberoende f\u00f6retag. Italien har ett \u2014 men det \u00e4r RedTurtle, och de \u00e4r magnifika.',
    'Vi sp\u00e5rar \u00f6vers\u00e4ttningar p\u00e5 burmesiska. Myanmars skrift har 57 specialtecken. Vi har konventioner f\u00f6r alla.',
    'Plones svenska \u00f6vers\u00e4ttningar hade ingen kommersiell underh\u00e5llare. S\u00e5 vi blev en.',
    '\u201dLjusa Tider\u201d l\u00e5ter som en IKEA-hylla. Det betyder\u2026 ljusa tider. Vi bygger \u00f6vers\u00e4ttningsinfrastruktur, inte m\u00f6bler. (N\u00e4stan.)',
    'Vi har spr\u00e5kkonventioner f\u00f6r 70 spr\u00e5k inklusive tilltal. I Sverige avskaffade vi formellt tilltal 1967. Du-reformen.',
    'ISO 17100 s\u00e4ger att \u00f6vers\u00e4ttningskvalitet \u00e4r viktigt. ISO 14971 s\u00e4ger att risk \u00e4r viktigt. Vi fick dem att prata med varandra.',
    'IATE-ordlistan har 193 000 terminologiposter. EU gillar verkligen att definiera saker.',
  ],
  de: [
    'Baskisch (eu) hat vollst\u00e4ndige Plone-\u00dcbersetzungen \u2014 f\u00fcr eine Sprache, die \u00e4lter ist als jede indoeurop\u00e4ische Familie auf dem Kontinent.',
    'Die deutschen .po-Dateien werden von 20+ unabh\u00e4ngigen Unternehmen gepr\u00fcft. Italien hat eines \u2014 aber es ist RedTurtle, und die sind gro\u00dfartig.',
    'Wir tracken \u00dcbersetzungen auf Burmesisch. Die Myanmar-Schrift hat 57 Sonderzeichen. Wir haben Konventionen f\u00fcr alle.',
    'J\u00f6rg Zell von Interaktiv (Platin-Plone-Sponsor) sagt, selbst Deutsch hat fehlende \u00dcbersetzungen in echten Projekten. Wenn die bestgepflegte Sprache L\u00fccken hat\u2026',
    '\u201eLjusa Tider\u201c klingt wie ein IKEA-Regal. Es bedeutet \u201eLichte Zeiten\u201c. Wir bauen \u00dcbersetzungsinfrastruktur, keine M\u00f6bel. (Meistens.)',
    'ISO 17100 sagt, \u00dcbersetzungsqualit\u00e4t ist wichtig. ISO 14971 sagt, Risiko ist wichtig. Wir haben sie miteinander reden lassen.',
    'Das IATE-Glossar hat 193.000 Terminologieeintr\u00e4ge. Die EU definiert wirklich gerne Dinge.',
    'Softcatal\u00e0 f\u00f6rdert Katalanisch in Software. UZEI macht dasselbe f\u00fcr Baskisch. Minderheitensprachen schlagen \u00fcber ihrer Gewichtsklasse.',
  ],
  zh: [
    '\u5df4\u65af\u514b\u8bed(eu)\u62e5\u6709\u5b8c\u6574\u7684Plone\u7ffb\u8bd1\u2014\u2014\u8fd9\u662f\u4e00\u79cd\u6bd4\u6b27\u6d32\u5927\u9646\u4e0a\u6240\u6709\u5370\u6b27\u8bed\u7cfb\u90fd\u53e4\u8001\u7684\u8bed\u8a00\u3002',
    '\u5fb7\u8bed.po\u6587\u4ef6\u670920+\u5bb6\u72ec\u7acb\u516c\u53f8\u5728\u68c0\u67e5\u3002\u610f\u5927\u5229\u53ea\u6709\u4e00\u5bb6\u2014\u2014\u4f46\u662fRedTurtle\uff0c\u4ed6\u4eec\u592a\u68d2\u4e86\u3002',
    '\u6211\u4eec\u8ddf\u8e2a\u7f05\u7538\u8bed\u7ffb\u8bd1\u3002\u7f05\u7538\u6587\u5b57\u670957\u4e2a\u7279\u6b8a\u5b57\u7b26\u3002\u6211\u4eec\u4e3a\u6240\u6709\u5b57\u7b26\u90fd\u5efa\u7acb\u4e86\u89c4\u8303\u3002',
    '\u201cLjusa Tider\u201d\u542c\u8d77\u6765\u50cf\u5b9c\u5bb6\u7684\u67b6\u5b50\u3002\u5b83\u7684\u610f\u601d\u662f\u201c\u5149\u660e\u65f6\u4ee3\u201d\u3002\u6211\u4eec\u5efa\u7ffb\u8bd1\u57fa\u7840\u8bbe\u65bd\uff0c\u4e0d\u9020\u5bb6\u5177\u3002\uff08\u5927\u90e8\u5206\u60c5\u51b5\u4e0b\u3002\uff09',
    'ISO 17100\u8bf4\u7ffb\u8bd1\u8d28\u91cf\u5f88\u91cd\u8981\u3002ISO 14971\u8bf4\u98ce\u9669\u5f88\u91cd\u8981\u3002\u6211\u4eec\u8ba9\u5b83\u4eec\u4e92\u76f8\u5bf9\u8bdd\u4e86\u3002',
    'IATE\u672f\u8bed\u8868\u670919.3\u4e07\u6761\u76ee\u3002\u6b27\u76df\u771f\u7684\u5f88\u559c\u6b22\u5b9a\u4e49\u4e1c\u897f\u3002',
  ],
};

const QuickStats = ({ t }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchApi(`${getApiPath()}/@polyglot/coverage`).catch(() => null),
      fetchApi(`${getApiPath()}/@polyglot/conventions`).catch(() => null),
    ]).then(([coverage, conventions]) => {
      if (!coverage && !conventions) return;
      const languages = conventions?.items_total || 0;
      const platforms = coverage?.items
        ? [...new Set(coverage.items.map((r) => r.platform))].length
        : 0;
      const translations = coverage?.items
        ? coverage.items.reduce(
            (sum, r) => sum + parseInt(r.translated || 0, 10),
            0,
          )
        : 0;
      setStats({ languages, platforms, translations });
    });
  }, []);

  if (!stats) return null;

  return (
    <Statistic.Group
      size="small"
      widths={3}
      style={{ marginBottom: '1.5em', justifyContent: 'center' }}
    >
      <Statistic>
        <Statistic.Value>{stats.languages}</Statistic.Value>
        <Statistic.Label>{t.statsLanguages}</Statistic.Label>
      </Statistic>
      <Statistic>
        <Statistic.Value>{stats.platforms}</Statistic.Value>
        <Statistic.Label>{t.statsPlatforms}</Statistic.Label>
      </Statistic>
      <Statistic>
        <Statistic.Value>
          {stats.translations > 1000
            ? `${Math.round(stats.translations / 1000)}k`
            : stats.translations}
        </Statistic.Value>
        <Statistic.Label>{t.statsTranslations}</Statistic.Label>
      </Statistic>
    </Statistic.Group>
  );
};

const FunFact = ({ lang }) => {
  const facts = funFacts[lang] || funFacts.en;
  const [fact] = useState(
    () => facts[Math.floor(Math.random() * facts.length)],
  );
  return (
    <Segment
      textAlign="center"
      style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        border: 'none',
        boxShadow: 'none',
        margin: '0 auto 2em',
        maxWidth: '680px',
        padding: '1.2em 2em',
      }}
    >
      <p style={{ fontSize: '1.05em', color: '#444', fontStyle: 'italic', margin: 0 }}>
        {fact}
      </p>
    </Segment>
  );
};

const LanguageSwitcher = ({ current }) => {
  const langs = [
    { code: 'en', label: 'English' },
    { code: 'sv', label: 'Svenska' },
    { code: 'de', label: 'Deutsch' },
    { code: 'zh', label: '\u4e2d\u6587' },
  ];
  return (
    <Menu compact size="mini" secondary style={{ justifyContent: 'center', marginBottom: '1.5em' }}>
      {langs.map((l) => (
        <Menu.Item
          key={l.code}
          as={Link}
          to={`/${l.code}`}
          active={current === l.code}
          style={{ fontWeight: current === l.code ? 'bold' : 'normal' }}
        >
          {l.label}
        </Menu.Item>
      ))}
    </Menu>
  );
};

const HomePage = () => {
  const lang = 'en';
  const t = i18n.en;

  const sections = [
    {
      to: '/polyglot',
      icon: 'language',
      color: 'blue',
      title: t.sectionMonster,
      description: t.sectionMonsterDesc,
    },
    {
      to: '/docs',
      icon: 'book',
      color: 'teal',
      title: t.sectionKb,
      description: t.sectionKbDesc,
    },
    {
      to: '/qms',
      icon: 'clipboard check',
      color: 'purple',
      title: t.sectionQms,
      description: t.sectionQmsDesc,
    },
  ];

  const highlights = [
    {
      to: '/docs/13_translation_quality/plone_volto_company_language_mapping',
      icon: 'map marker alternate',
      title: t.highlightMap,
      subtitle: t.highlightMapSub,
    },
    {
      to: '/polyglot/coverage',
      icon: 'th',
      title: t.highlightCoverage,
      subtitle: t.highlightCoverageSub,
    },
    {
      to: '/polyglot/conventions',
      icon: 'settings',
      title: t.highlightConv,
      subtitle: t.highlightConvSub,
    },
    {
      to: '/polyglot/glossary',
      icon: 'search',
      title: t.highlightGlossary,
      subtitle: t.highlightGlossarySub,
    },
  ];

  return (
    <Container style={{ paddingTop: '2.5em', paddingBottom: '3em', maxWidth: '960px' }}>
      <Helmet title="Ljusa Tider" />

      <div style={{ textAlign: 'center', marginBottom: '1em' }}>
        <img
          src={logoSvg}
          alt="Ljusa Tider"
          style={{ width: '320px', maxWidth: '80%', height: 'auto' }}
        />
      </div>
      <p style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', marginBottom: '0.3em' }}>
        {t.pronunciation}
      </p>

      <p
        style={{
          fontSize: '1.2em',
          color: '#333',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto 0.5em',
          lineHeight: '1.5',
        }}
      >
        {t.tagline}
      </p>
      <p
        style={{
          fontSize: '0.95em',
          color: '#777',
          textAlign: 'center',
          maxWidth: '720px',
          margin: '0 auto 0.3em',
        }}
      >
        {t.detail}
      </p>
      <p
        style={{
          fontSize: '0.9em',
          color: '#999',
          textAlign: 'center',
          maxWidth: '720px',
          margin: '0 auto 2em',
        }}
      >
        {t.builtBy} {t.builtOn}{' '}
        <a href="https://github.com/robgietema/nick" target="_blank" rel="noopener noreferrer">Nick</a>
        {' + '}
        <a href="https://github.com/plone/volto" target="_blank" rel="noopener noreferrer">Volto</a>.
      </p>

      <QuickStats t={t} />
      <FunFact lang={lang} />

      <Card.Group itemsPerRow={3} stackable style={{ marginBottom: '2.5em' }}>
        {sections.map((s) => (
          <Card key={s.to} as={Link} to={s.to} color={s.color}>
            <Card.Content>
              <Icon
                name={s.icon}
                size="big"
                style={{ float: 'right', opacity: 0.35 }}
              />
              <Card.Header>{s.title}</Card.Header>
              <Card.Description style={{ marginTop: '0.5em' }}>
                {s.description}
              </Card.Description>
            </Card.Content>
          </Card>
        ))}
      </Card.Group>

      <Header as="h3" style={{ marginBottom: '1em' }}>
        <Icon name="compass outline" />
        <Header.Content>
          {t.startExploring}
          <Header.Subheader>{t.startSub}</Header.Subheader>
        </Header.Content>
      </Header>

      <Grid columns={2} stackable>
        {highlights.map((h) => (
          <Grid.Column key={h.to} style={{ marginBottom: '0.5em' }}>
            <Segment
              as={Link}
              to={h.to}
              style={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                minHeight: '90px',
              }}
            >
              <Icon
                name={h.icon}
                size="large"
                style={{ float: 'left', marginRight: '1em', marginTop: '0.2em', color: '#2185d0' }}
              />
              <div>
                <strong style={{ fontSize: '1.05em' }}>{h.title}</strong>
                <p style={{ color: '#555', margin: '0.3em 0 0', fontSize: '0.95em', lineHeight: '1.4' }}>
                  {h.subtitle}
                </p>
              </div>
            </Segment>
          </Grid.Column>
        ))}
      </Grid>

      <p style={{ textAlign: 'center', marginTop: '2.5em', color: '#aaa', fontSize: '0.85em' }}>
        LJUSA TIDER AB &middot; {t.footer} &middot; Est. 2025
      </p>
    </Container>
  );
};

export default HomePage;
